import { type Group, type Object3D, Vector3 } from 'three';

import { resolveAddedParent } from '../../managers';
import type { SceneDiff, SceneManager } from '../../managers';
import type { SceneComposition } from '../../types';
import type { KitchenSceneBuilder } from '../../builders/kitchen-scene-builder';
import type { ISceneBuilder } from '../../builders';

// ========== hasStructuralChangesCfn ==========

const STRUCTURAL_TYPES = ['island', 'panel', 'countertop', 'floor', 'trim', 'surround', 'corner'];

/**
 * Check if diff contains structural changes that require full rebuild.
 * Structural = islands, panels, countertops, floor.
 * Non-structural = appliances (can be animated).
 */
export const hasStructuralChangesCfn = (diff: SceneDiff): boolean => {
	const structuralAdded = diff.added.filter((obj) => STRUCTURAL_TYPES.includes(obj.metadata.type));
	if (structuralAdded.length > 0) return true;

	const structuralRemoved = diff.removed.filter((snapshot) => STRUCTURAL_TYPES.includes(snapshot.type));
	if (structuralRemoved.length > 0) return true;

	const structuralMoved = diff.moved.filter((move) => STRUCTURAL_TYPES.includes(move.metadata.type));
	if (structuralMoved.length > 0) return true;

	return false;
};

// ========== mergeCompositionsCfn ==========

/**
 * Merge multiple compositions into one.
 */
export const mergeCompositionsCfn = (compositions: Array<SceneComposition>): SceneComposition => {
	const allObjects = compositions.flatMap((c) => c.objects);

	const camera = compositions.find((c) => c.camera)?.camera;

	let mergedBounds: SceneComposition['bounds'] | undefined;
	for (const comp of compositions) {
		if (comp.bounds) {
			if (!mergedBounds) {
				mergedBounds = { ...comp.bounds };
			} else {
				mergedBounds.min.x = Math.min(mergedBounds.min.x, comp.bounds.min.x);
				mergedBounds.min.y = Math.min(mergedBounds.min.y, comp.bounds.min.y);
				mergedBounds.min.z = Math.min(mergedBounds.min.z, comp.bounds.min.z);
				mergedBounds.max.x = Math.max(mergedBounds.max.x, comp.bounds.max.x);
				mergedBounds.max.y = Math.max(mergedBounds.max.y, comp.bounds.max.y);
				mergedBounds.max.z = Math.max(mergedBounds.max.z, comp.bounds.max.z);
			}
		}
	}

	return {
		objects: allObjects,
		bounds: mergedBounds,
		camera
	};
};

// ========== applyDiffImmediateCfn ==========

/**
 * Apply a diff immediately without animations.
 */
export const applyDiffImmediateCfn = (diff: SceneDiff, objectHolder: Object3D): void => {
	// Remove deleted objects
	for (const snapshot of diff.removed) {
		if (snapshot.object.parent) {
			snapshot.object.parent.remove(snapshot.object);
		}
	}

	// Move objects. `move.to` is a WORLD position (computeDiff reads it via
	// getWorldPosition), but object.position is LOCAL to the parent island group —
	// convert, or the island's origin and rotation get applied twice. Same defect the
	// animated path in SceneTransitionManager.animateMoves carries.
	for (const move of diff.moved) {
		const target = new Vector3(move.to.x, move.to.y, move.to.z);
		if (move.object.parent) {
			move.object.parent.updateWorldMatrix(true, false);
			move.object.parent.worldToLocal(target);
		}
		move.object.position.copy(target);
	}

	// Add new objects, each under the island it was built into — see
	// `resolveAddedParent`. Attaching an island-owned appliance to the holder
	// instead reparents it out of its island, which strands it there as a ghost
	// the next rebuild cannot remove.
	for (const sceneObject of diff.added) {
		sceneObject.object.userData = {
			...sceneObject.object.userData,
			...sceneObject.metadata
		};
		resolveAddedParent(objectHolder, sceneObject.object).add(sceneObject.object);
	}
};

// ========== regenerateCutoutsForCurrentSceneCfn ==========

export type RegenerateCutoutsParams = {
	sceneManager: SceneManager | null;
	builders: Map<string, ISceneBuilder>;
};

/**
 * Regenerate countertop/surround cutouts on the current scene islands after appliance changes.
 */
export const regenerateCutoutsForCurrentSceneCfn = async (params: RegenerateCutoutsParams): Promise<void> => {
	const { sceneManager, builders } = params;

	const objectHolder = sceneManager?.objectHolder;
	if (!objectHolder) {
		console.warn('ThreeSceneEngine: Cannot regenerate cutouts - missing objectHolder');
		return;
	}

	const kitchenBuilder = builders.get('kitchen') as KitchenSceneBuilder | undefined;
	if (!kitchenBuilder?.regenerateCutoutsForIslands) {
		console.warn('ThreeSceneEngine: Kitchen builder not available for cutout regeneration');
		return;
	}

	const islandGroups = objectHolder.children.filter((child) => child.userData?.type === 'island') as Array<Group>;

	if (islandGroups.length === 0) {
		console.warn('ThreeSceneEngine: No island groups found for cutout regeneration');
		return;
	}

	const debugGroup = await kitchenBuilder.regenerateCutoutsForIslands(islandGroups);

	// Replace any existing debug group
	const existingDebug = objectHolder.getObjectByName('cutout-debug-group');
	if (existingDebug) {
		existingDebug.parent?.remove(existingDebug);
	}
	if (debugGroup) {
		objectHolder.add(debugGroup);
	}
};
